import React from 'react';

/**
 * TeamPanel — who's on it (Epic 8 · S8.2, Part A.3).
 *
 * Avatar row: avatar (or first initial on brand) + first name; "AI assistant"
 * marker when `kind==='agent'`. Click → `portalDrill('person', id, {park_id})`
 * (`onOpenMember`). No roles/hours. 0 → sentence; >12 → 12 + "+N".
 *
 * Presentational — never imports the data layer (Epic 1).
 */
const BRAND = '#32B1FF';

const firstName = (m) => {
  const name = m?.name || m?.label || '?';
  return String(name).split(/\s+/)[0];
};

const Avatar = ({ member }) => {
  if (member?.avatar) {
    return <img src={member.avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />;
  }
  return (
    <span className="w-7 h-7 rounded-full text-white text-xs font-semibold flex items-center justify-center shrink-0" style={{ backgroundColor: BRAND }}>
      {(member?.name || member?.label || '?').charAt(0)}
    </span>
  );
};

const TeamPanel = ({ facts, onOpenMember }) => {
  const members = Array.isArray(facts?.members) ? facts.members : [];

  if (members.length === 0) {
    return (
      <section data-testid="panel-team" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Team</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">No team members assigned yet.</p>
      </section>
    );
  }

  const MAX = 12;
  const shown = members.slice(0, MAX);
  const overflow = members.length - shown.length;

  return (
    <section data-testid="panel-team" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Team</h3>
      <ul className="flex flex-wrap gap-2">
        {shown.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => onOpenMember?.(m)}
              aria-label={`Open ${m.label || m.name}`}
              data-testid="team-member"
              data-member-id={m.id}
              className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-800 py-1 pl-1 pr-3 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
            >
              <Avatar member={m} />
              <span className="flex items-center gap-1.5">
                <span className="text-sm text-gray-800 dark:text-gray-100">{firstName(m)}</span>
                {m.kind === 'agent' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200" data-testid="team-agent-marker">
                    AI assistant
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
        {overflow > 0 && (
          <li>
            <span className="inline-flex items-center px-3 py-1 text-sm text-gray-500 dark:text-gray-400" data-testid="team-overflow">
              +{overflow}
            </span>
          </li>
        )}
      </ul>
    </section>
  );
};

export default TeamPanel;
